import 'shared/styles/gate.css';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchMaterialsRequest } from 'shared/redux/actions/MaterialsActions';
import { io } from 'socket.io-client';
import { Image } from 'react-bootstrap';
import {
  getDestinationsState,
  getMaterialsState,
  getOptionsState,
  getOrderState,
} from '../redux/src/StatesGetter';
import { createOrderRequest } from '../redux/actions/OrderActions';
import socketService from '../services/SocketService';
import LicensePlate from '../services/LicencePlateService';
import euFlag from '../assets/images/eu_flag_circle.png';
import Alerts from '../components/Alerts';
import image from '../assets/images/logo_agrewest.png';
import alerts from '../components/Alerts';
import { fetchOptionsRequest } from '../redux/actions/OptionsActions';
import { fetchDestinationsRequest } from '../redux/actions/DestinationsActions';
import ReloadButton from '../components/ReloadButton';
import ApiUtils from '../modules/ApiUtils';
import { default as ApiConstants } from 'shared/constants/ApiConstants.json';
import { getEnv } from '../services/EnvService';

/** Optional kiosk token: REACT_APP_MANUAL_PLATE_API_TOKEN (live backend often requires this). */
const manualPlateApiHeaders = (): Record<string, string> | null => {
  const token = getEnv('MANUAL_PLATE_API_TOKEN');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

const MainPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedMaterial, setSelectedMaterial] = useState();
  const [selectedQuantity, setSelectedQuantity] = useState('max');
  const materials = useSelector(getMaterialsState, shallowEqual);
  const options = useSelector(getOptionsState, shallowEqual);
  const destinations = useSelector(getDestinationsState, shallowEqual);
  const [vehicleData, setVehicleData] = useState({ numberPlate: '', _id: '' });
  const [scaleEventData, setScaleEventData] = useState({ value: 0, state: '' });
  const [orderEventData, setOrderEventData] = useState({ action: '' });
  const [step, setStep] = useState(1);
  const [currentDriver, setCurrentDriver] = useState();
  const [currentDestination, setCurrentDestination] = useState();
  const [currentBuyer, setCurrentBuyer] = useState();
  const [currentTrailer, setCurrentTrailer] = useState('');
  const [alertVisible, setAlertVisible] = useState(false);
  const [isEditingPlate, setIsEditingPlate] = useState(false);
  const [editedPlateValue, setEditedPlateValue] = useState('');
  const [plateConfirmed, setPlateConfirmed] = useState(false);
  const [showManualPlateEntry, setShowManualPlateEntry] = useState(false);
  const [manualPlateValue, setManualPlateValue] = useState('');

  interface EventData {
    action?: string;
    vehicle?: any;
    order?: any;
  }

  const bottomRef = useRef<HTMLDivElement>(null);
  const [eventData, setEventData] = useState<EventData>({});
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle inactivity timeout - reload page after 30 seconds of no activity (only if vehicleData exists and not in final_load state)
  useEffect(() => {
    const resetTimer = () => {
      // Clear existing timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      // Only set timer if vehicleData exists and action is not 'final_load'
      if (vehicleData?.numberPlate && eventData?.action !== 'final_load') {
        // Set new timer for 30 seconds
        inactivityTimerRef.current = setTimeout(() => {
          window.location.reload();
        }, 30000); // 30 seconds
      }
    };

    // Initial timer start
    resetTimer();

    // Activity events that should reset the timer
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    // Cleanup
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [vehicleData, eventData]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyHeight = body.style.height;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.height = '100%';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.height = prevHtmlHeight;
      body.style.height = prevBodyHeight;
    };
  }, []);

  useEffect(() => {
    socketService.connect().then(() => {
      console.log('Socket connected!');
      socketService.on('vehicle', (data) => {
        console.log('socketEventData', data);
        setEventData(data);
        if (data?.vehicle) {
          setVehicleData(data?.vehicle);
          setPlateConfirmed(false);
          setIsEditingPlate(false);
          dispatch(fetchOptionsRequest({ licencePlate: data.vehicle.numberPlate }));
        }
      });

      socketService.on('scale', (scaleData) => {
        console.log('scaleData', scaleData);
        if (scaleData?.state !== 'idle') {
          setScaleEventData(scaleData);
        }
      });

      socketService.on('order', (orderData) => {
        console.log('orderData', orderData);
        setOrderEventData(orderData);
        {
          orderData.action == 'completed' && handlePrintDocuments();
        }
      });
    });
    return () => {
      console.log('Socket disconnected!');
      socketService.disconnect();
    };
  }, []);

  useEffect(() => {
    dispatch(fetchMaterialsRequest());
  }, []);

  useEffect(() => {
    if (alertVisible) {
      // Your logic when the alert is visible
    }
  }, [alertVisible]);

  useEffect(() => {
    // Auto-select driver if there's only one in the list
    if (options?.drivers && options.drivers.length === 1 && !currentDriver) {
      setCurrentDriver(options.drivers[0].id);
    }
  }, [options?.drivers, currentDriver]);

  useEffect(() => {
    // Auto-select trailer if there's only one in the list
    if (options?.trailers && options.trailers.length === 1 && !currentTrailer) {
      setCurrentTrailer(options.trailers[0]);
    }
  }, [options?.trailers, currentTrailer]);

  useEffect(() => {
    // Auto-select buyer if there's only one in the list
    if (options?.buyers && options.buyers.length === 1 && !currentBuyer) {
      const buyer = options.buyers[0];
      console.log('Auto-selecting buyer:', buyer);
      setCurrentBuyer(buyer.id);
      dispatch(fetchDestinationsRequest({ buyerId: buyer.id }));
    }
  }, [options?.buyers, currentBuyer, dispatch]);

  useEffect(() => {
    // Auto-select destination if there's only one in the list
    if (destinations?.destinations && destinations.destinations.length === 1 && !currentDestination) {
      const destination = destinations.destinations[0];
      console.log('Auto-selecting destination:', destination);
      setCurrentDestination(destination.lDestinatie);
    }
  }, [destinations?.destinations, currentDestination]);

  useEffect(() => {
    if (materials?.length === 1 && selectedMaterial == null) {
      setSelectedMaterial(materials[0].internalId);
    }
  }, [materials, selectedMaterial]);

  const handleCurrentDriver = async (driver) => {
    setCurrentDriver(driver.id);
  };

  const handleCurrentDestination = async (destination) => {
    setCurrentDestination(destination.lDestinatie);
  };

  const handlePrintDocuments = async () => {
    alerts.textAlert('Succes', 'Incarcarea a fost finalizata cu succes');
    setAlertVisible(true);
    setTimeout(() => {
      setAlertVisible(false);
      window.location.reload();
    }, 5000);
  };
  const handleCurrentBuyer = async (buyer) => {
    console.log('handleCurrentBuyer called with:', buyer);
    setCurrentBuyer(buyer.id);
    dispatch(fetchDestinationsRequest({ buyerId: buyer.id }));
  };
  const handleCurrentTrailer = async (trailer) => {
    setCurrentTrailer(trailer);
  };

  const handleNextStep = async (step, current) => {
    if (!current) {
      alerts.errorAlert('Va rugam selectati o optiune');
      return;
    }

    setStep(step);
  };

  const handleOrderClick = async () => {
    if (!selectedMaterial) {
      alerts.errorAlert('Va rugam selectati o optiune');
      return;
    }
    setStep(5);

    dispatch(
      createOrderRequest({
        quantity: 0,
        fullLoad: true,
        vehicle: vehicleData._id,
        driverId: currentDriver,
        buyerId: currentBuyer,
        productId: selectedMaterial,
        destinationId: currentDestination,
        trailerLicensePlate: currentTrailer,
      }),
    );
  };

  const handleConfirmPlate = () => {
    setPlateConfirmed(true);
  };

  const handleStartEditPlate = () => {
    setEditedPlateValue((vehicleData.numberPlate || '').toUpperCase());
    setIsEditingPlate(true);
  };

  const handleSaveEditedPlate = async () => {
    const newPlate = editedPlateValue?.trim() || '';
    if (!newPlate) {
      alerts.errorAlert('Introduceți numărul de înmatriculare');
      return;
    }
    const vehicle = { numberPlate: newPlate, _id: '' };
    setVehicleData(vehicle);
    dispatch(fetchOptionsRequest({ licencePlate: newPlate }));
    setIsEditingPlate(false);
    setPlateConfirmed(false);
    setEditedPlateValue('');
    try {
      await ApiUtils.postRequest(
        ApiConstants.MANUAL_PLATE_EVENT_ENDPOINT,
        { numberPlate: newPlate },
        manualPlateApiHeaders(),
      );
      // Server will emit 'vehicle' event; existing socket listener will update state (e.g. vehicle._id, order)
    } catch (err: unknown) {
      console.error('Manual plate event failed:', err);
      const apiMessage =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: unknown }).message === 'string'
          ? (err as { message: string }).message
          : err instanceof Error
            ? err.message
            : '';
      alerts.errorAlert(
        apiMessage || 'Eroare la trimiterea numărului de înmatriculare',
      );
    }
  };

  const handleOpenManualPlateEntry = () => {
    setManualPlateValue('');
    setShowManualPlateEntry(true);
  };

  const handleCloseManualPlateEntry = () => {
    setShowManualPlateEntry(false);
    setManualPlateValue('');
  };

  const handleSubmitManualPlate = async () => {
    const newPlate = manualPlateValue?.trim() || '';
    if (!newPlate) {
      alerts.errorAlert('Introduceți numărul de înmatriculare');
      return;
    }
    const vehicle = { numberPlate: newPlate, _id: '' };
    setVehicleData(vehicle);
    dispatch(fetchOptionsRequest({ licencePlate: newPlate }));
    setEventData((prev) => ({ ...prev, action: 'initial_load' }));
    setShowManualPlateEntry(false);
    setManualPlateValue('');
    setPlateConfirmed(false);
    try {
      await ApiUtils.postRequest(
        ApiConstants.MANUAL_PLATE_EVENT_ENDPOINT,
        { numberPlate: newPlate },
        manualPlateApiHeaders(),
      );
      // Server will emit 'vehicle' event; existing socket listener will update state (e.g. vehicle._id, order)
    } catch (err: unknown) {
      console.error('Manual plate event failed:', err);
      const apiMessage =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: unknown }).message === 'string'
          ? (err as { message: string }).message
          : err instanceof Error
            ? err.message
            : '';
      alerts.errorAlert(
        apiMessage || 'Eroare la trimiterea numărului de înmatriculare',
      );
    }
  };

  console.log('vehicleData', vehicleData);
  console.log('Event data:', eventData);
  console.log('orderEventData:', orderEventData);
  console.log('materials:', materials);
  console.log('destinations:', destinations.destinations);
  const isWaitingForPlateOnly = !vehicleData.numberPlate && !showManualPlateEntry;
  return (
    <div
      className={`gate-page gate-page-kiosk${isWaitingForPlateOnly ? ' gate-page-kiosk--waiting-plate' : ''}`}
    >
      <main>
        <div className="container-fluid p-0">
          <section>
            <div
              className="container"
              style={
                !vehicleData.numberPlate
                  ? { height: '100%' }
                  : !plateConfirmed || isEditingPlate
                    ? { paddingTop: 8, marginTop: 8 }
                    : {}
              }
            >
              <div className="row justify-content-end" style={!vehicleData.numberPlate ? { height: '100%', margin: 0 } : {}}>
                <div
                  className="col-lg-12 col-xl-12 col-md-12 col-12"
                  style={
                    !vehicleData.numberPlate ? { height: '100%', padding: 0 } : !plateConfirmed || isEditingPlate ? { paddingTop: 0 } : {}
                  }
                >
                  {!(
                    vehicleData.numberPlate &&
                    (!plateConfirmed || isEditingPlate)
                  ) && (
                    <div
                      className="gate-introdu-numar-top"
                      style={{
                        position: 'absolute',
                        top: 25,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 100,
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleOpenManualPlateEntry}
                        style={{
                          padding: '13px 25px',
                          minHeight: 40,
                          backgroundColor: '#2196f3',
                          color: 'white',
                          fontSize: 25,
                          fontWeight: 'bold',
                          border: 'none',
                          borderRadius: 10,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Introdu număr inmatriculare
                      </button>
                    </div>
                  )}
                  <div
                    className="top-right-buttons"
                    style={{
                      position: 'absolute',
                      top: 25,
                      right: 10,
                      zIndex: 100,
                    }}
                  >
                    <ReloadButton />
                  </div>
                  {showManualPlateEntry && (
                    <div
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: 20,
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: 'white',
                          borderRadius: 12,
                          padding: 32,
                          maxWidth: 960,
                          width: '95%',
                          minWidth: 320,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 36,
                            fontWeight: 'bold',
                            marginBottom: 20,
                            textAlign: 'center',
                          }}
                        >
                          Introdu numărul de înmatriculare
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            height: 120,
                            backgroundColor: 'white',
                            borderRadius: 10,
                            border: '1px solid #000',
                            boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
                            fontFamily: 'Arial, sans-serif',
                            fontSize: 100,
                            fontWeight: 'bold',
                            marginBottom: 20,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              flexDirection: 'column',
                              width: 120,
                              height: 120,
                              backgroundColor: '#004AAD',
                              marginRight: 15,
                              borderBottomLeftRadius: 8,
                              borderTopLeftRadius: 8,
                              marginLeft: 0,
                            }}
                          >
                            <img src={euFlag} alt="" style={{ width: 60, height: 60 }} />
                            <div style={{ color: 'white', fontWeight: 'normal', fontSize: 40 }}>RO</div>
                          </div>
                          <input
                            type="text"
                            value={manualPlateValue}
                            onChange={(e) => setManualPlateValue(e.target.value.toUpperCase())}
                            placeholder="Număr"
                            aria-label="Număr înmatriculare"
                            style={{
                              flex: 1,
                              height: '100%',
                              padding: '0 15px',
                              border: 'none',
                              outline: 'none',
                              fontFamily: 'Arial, sans-serif',
                              fontSize: 100,
                              fontWeight: 'bold',
                              backgroundColor: 'transparent',
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                          <button
                            type="button"
                            className="formButton"
                            onClick={handleCloseManualPlateEntry}
                            style={{
                              height: 96,
                              minHeight: 96,
                              margin: 0,
                              boxSizing: 'border-box',
                              fontSize: 36,
                              padding: '0 48px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            Anulare
                          </button>
                          <button
                            type="button"
                            className="placeOrderButton"
                            onClick={handleSubmitManualPlate}
                            style={{
                              height: 96,
                              minHeight: 96,
                              margin: 0,
                              boxSizing: 'border-box',
                              backgroundColor: '#22c55e',
                              fontSize: 36,
                              padding: '0 48px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            Salvează
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {!vehicleData.numberPlate && !showManualPlateEntry && (
                    <div className="gate-waiting-plate-message">
                      Numar de inmatriculare neidentificabil
                    </div>
                  )}
                  {vehicleData.numberPlate && (
                    <>
                      <div
                        className="orderForm"
                        style={
                          !plateConfirmed || isEditingPlate
                            ? {
                                paddingTop: 60,
                                paddingLeft: 20,
                                paddingRight: 20,
                                paddingBottom: 20,
                                marginTop: 0,
                                minHeight: 'auto',
                                justifyContent: 'flex-start',
                              }
                            : undefined
                        }
                      >
                        {(!plateConfirmed || isEditingPlate) && (
                          <>
                            <div className="title" style={{ textAlign: 'center', marginBottom: 8, marginTop: 0 }}>
                              Foaie de comandă
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                width: '100%',
                                padding: 0,
                                marginBottom: 0,
                              }}
                            >
                            {isEditingPlate ? (
                              <>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    height: 120,
                                    backgroundColor: 'white',
                                    borderRadius: 10,
                                    border: '1px solid #000',
                                    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: 100,
                                    fontWeight: 'bold',
                                    marginBottom: 10,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      flexDirection: 'column',
                                      width: 120,
                                      height: 120,
                                      backgroundColor: '#004AAD',
                                      marginRight: 15,
                                      borderBottomLeftRadius: 8,
                                      borderTopLeftRadius: 8,
                                      marginLeft: 0,
                                    }}
                                  >
                                    <img src={euFlag} alt="" style={{ width: 60, height: 60 }} />
                                    <div style={{ color: 'white', fontWeight: 'normal', fontSize: 40 }}>RO</div>
                                  </div>
                                  <input
                                    type="text"
                                    value={editedPlateValue}
                                    onChange={(e) => setEditedPlateValue(e.target.value.toUpperCase())}
                                    placeholder="Număr"
                                    aria-label="Număr înmatriculare"
                                    style={{
                                      flex: 1,
                                      height: '100%',
                                      padding: '0 15px',
                                      border: 'none',
                                      outline: 'none',
                                      fontFamily: 'Arial, sans-serif',
                                      fontSize: 100,
                                      fontWeight: 'bold',
                                      backgroundColor: 'transparent',
                                    }}
                                  />
                                </div>
                                <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'stretch' }}>
                                  <button
                                    type="button"
                                    className="formButton"
                                    onClick={() => setIsEditingPlate(false)}
                                    style={{
                                      height: 96,
                                      minHeight: 96,
                                      margin: 0,
                                      boxSizing: 'border-box',
                                      fontSize: 36,
                                      padding: '0 48px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    Anulare
                                  </button>
                                  <button
                                    type="button"
                                    className="placeOrderButton"
                                    onClick={handleSaveEditedPlate}
                                    style={{
                                      height: 96,
                                      minHeight: 96,
                                      margin: 0,
                                      boxSizing: 'border-box',
                                      backgroundColor: '#22c55e',
                                      fontSize: 36,
                                      padding: '0 48px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    Salvează
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <LicensePlate licenceNumber={vehicleData?.numberPlate} />
                                <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'stretch' }}>
                                  <button
                                    type="button"
                                    className="formButton"
                                    onClick={handleStartEditPlate}
                                    style={{
                                      height: 120,
                                      minHeight: 120,
                                      margin: 0,
                                      boxSizing: 'border-box',
                                      fontSize: 45,
                                      padding: '0 55px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    Editează
                                  </button>
                                  <button
                                    type="button"
                                    className="placeOrderButton"
                                    onClick={handleConfirmPlate}
                                    style={{
                                      height: 120,
                                      minHeight: 120,
                                      margin: 0,
                                      boxSizing: 'border-box',
                                      backgroundColor: '#22c55e',
                                      fontSize: 45,
                                      padding: '0 55px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    Validează
                                  </button>
                                </div>
                              </>
                            )}
                            </div>
                          </>
                        )}
                        {plateConfirmed && !isEditingPlate && (
                          <>
                            <div className="title">Foaie de comandă</div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginRight: 40,
                              }}
                            >
                              <div>
                                <LicensePlate licenceNumber={vehicleData?.numberPlate} />
                              </div>
                              <div>
                                <div>
                                  <strong>Tara:</strong>{' '}
                                  {eventData?.order
                                    ? eventData?.order?.weightOnArrival / 1000
                                    : scaleEventData?.value / 1000}{' '}
                                  t
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                        {eventData?.action === 'final_load' && plateConfirmed && !isEditingPlate && (
                          <>
                            <div>
                              <strong>Sort comandat:</strong>{' '}
                              {eventData?.order?.material || 'Not Assigned'}
                            </div>
                            <div className="finalLoad">
                              <div className="finalLoadItem">
                                <div className="finalLoadTitle">ÎNCĂRCARE PRIMARĂ</div>
                                <div className="finalLoadValue">
                                  {eventData?.order?.quantity / 1000} t
                                </div>
                              </div>

                              <div className="finalLoadItem">
                                <div className="finalLoadTitle">DE SUPLIMENTAT</div>
                                <div className="finalLoadValue">
                                  {(eventData?.order?.maxWeight - scaleEventData.value) / 1000} t{' '}
                                </div>
                              </div>
                            </div>
                            <div className="finalLoadTitle">CANTITATE INCARCATĂ</div>
                            <div className="finalLoadValue">{scaleEventData?.value / 1000} t</div>
                          </>
                        )}

                        {eventData?.action === 'initial_load' && plateConfirmed && step === 1 && (
                          <>
                            <div className="title" style={{ textAlign: 'center' }}>
                              Sunt soferul{' '}
                            </div>
                            <div className="listDisplay">
                              {options?.drivers?.map((driver, index) => (
                                <>
                                  <button
                                    className={`quantityButton ${
                                      currentDriver === driver.id ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                      handleCurrentDriver(driver);
                                    }}
                                  >
                                    <div>
                                      <div>{driver.value}</div>
                                    </div>
                                  </button>
                                </>
                              ))}
                            </div>
                            <div ref={bottomRef} className="buttonContainer">
                              <button
                                type="button"
                                className="placeOrderButton"
                                disabled={!currentDriver}
                                onClick={() => {
                                  handleNextStep(2, currentDriver);
                                }}
                              >
                                CONTINUA
                              </button>
                            </div>
                          </>
                        )}

                        {eventData?.action === 'initial_load' && plateConfirmed && step === 2 && (
                          <>
                            <div className="title" style={{ textAlign: 'center' }}>
                              Numar remorca
                            </div>
                            <div className="listDisplay">
                              {options?.trailers?.map((trailer, index) => (
                                <>
                                  <button
                                    className={`quantityButton ${
                                      currentTrailer === trailer ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                      handleCurrentTrailer(trailer);
                                    }}
                                  >
                                    <div>
                                      <div>{trailer}</div>
                                    </div>
                                  </button>
                                </>
                              ))}
                            </div>
                            <div ref={bottomRef} className="buttonContainer">
                              <button type="button" className="formButton" onClick={() => setStep(1)}>
                                Inapoi
                              </button>
                              <button
                                type="button"
                                className="formButton"
                                disabled={!currentTrailer}
                                onClick={() => handleNextStep(3, currentTrailer)}
                              >
                                CONTINUA
                              </button>
                            </div>
                          </>
                        )}

                        {eventData?.action === 'initial_load' && plateConfirmed && step === 3 && (
                          <>
                            <div className="title" style={{ textAlign: 'center' }}>
                              Cumpar pentru
                            </div>
                            <div className="listDisplay">
                              {options?.buyers?.map((buyer, index) => (
                                <>
                                  <button
                                    className={`quantityButton ${
                                      currentBuyer === buyer.id ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                      handleCurrentBuyer(buyer);
                                    }}
                                  >
                                    <div>
                                      <div>{buyer.value}</div>
                                    </div>
                                  </button>
                                </>
                              ))}
                            </div>
                            <div ref={bottomRef} className="buttonContainer">
                              <button type="button" className="formButton" onClick={() => setStep(2)}>
                                Inapoi
                              </button>
                              <button
                                type="button"
                                className="formButton"
                                disabled={!currentBuyer}
                                onClick={() => handleNextStep(4, currentBuyer)}
                              >
                                CONTINUA
                              </button>
                            </div>
                          </>
                        )}

                        {eventData?.action === 'initial_load' && plateConfirmed && step === 4 && (
                          <>
                            <div className="title" style={{ textAlign: 'center' }}>
                              Destinatia
                            </div>
                            <div className="listDisplay">
                              {destinations.destinations?.map((destination, index) => (
                                <>
                                  <button
                                    className={`quantityButton ${
                                      currentDestination === destination.lDestinatie ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                      handleCurrentDestination(destination);
                                    }}
                                  >
                                    <div>
                                      <div>{destination.denumireLocalitateDescarcare}</div>
                                      {/* <div>{material.size}</div> */}
                                    </div>
                                  </button>
                                </>
                              ))}
                            </div>
                            <div ref={bottomRef} className="buttonContainer">
                              <button type="button" className="formButton" onClick={() => setStep(3)}>
                                Inapoi
                              </button>
                              <button
                                type="button"
                                className="formButton"
                                disabled={!currentDestination}
                                onClick={() => handleNextStep(5, currentDestination)}
                              >
                                CONTINUA
                              </button>
                            </div>
                          </>
                        )}

                        {eventData?.action === 'initial_load' && plateConfirmed && step === 5 && (
                          <>
                            <div className="title" style={{ textAlign: 'center' }}>
                              Vreau sa comand
                            </div>
                            <div className="listDisplay">
                              {materials?.map((material, index) => (
                                <>
                                  <button
                                    className={`quantityButton ${
                                      selectedMaterial === material.internalId ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                      setSelectedMaterial(material.internalId);
                                    }}
                                  >
                                    <div>
                                      <div>{material.name}</div>
                                      <div>{material.size}</div>
                                    </div>
                                  </button>
                                </>
                              ))}
                            </div>
                            <div ref={bottomRef} className="buttonContainer">
                              <button type="button" className="formButton" onClick={() => setStep(4)}>
                                Inapoi
                              </button>
                              <button
                                type="button"
                                className="formButton"
                                disabled={!selectedMaterial}
                                onClick={() => handleOrderClick()}
                              >
                                PLASARE COMANDĂ
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default MainPage;
